import type { Logger } from "@/utils/logger";
import { theme } from "@/utils/theme";

export class AuthModalManager {
  #logger: Logger;
  #overlay: HTMLElement | null = null;
  #escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  #previouslyFocused: HTMLElement | null = null;
  #previousOverflow: string = "";

  constructor(logger: Logger) {
    this.#logger = logger.extend(this.constructor.name);
  }

  get isOpen(): boolean {
    return this.#overlay !== null;
  }

  open(authModalUrl: string) {
    if (this.isOpen) {
      this.#logger.warn("Auth modal is already open");
      return;
    }

    this.#logger.debug("Opening auth modal", { authModalUrl });

    this.#previouslyFocused = document.activeElement as HTMLElement;

    this.#previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const overlay = document.createElement("div");
    overlay.id = "fanfest-auth-modal-overlay";
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 2147483647;
      background: ${theme.colors.overlayBackground};
      display: flex; align-items: center; justify-content: center;
    `;

    const container = document.createElement("div");
    container.id = "fanfest-auth-modal-container";
    container.style.cssText = `
      width: 420px; max-width: 95vw;
      height: 600px; max-height: 90vh;
      border-radius: 12px; overflow: hidden;
      background: ${theme.colors.modalBackground}; box-shadow: 0 25px 50px rgba(0,0,0,0.25);
      position: relative;
    `;

    const loadingIndicator = document.createElement("div");
    loadingIndicator.id = "fanfest-auth-modal-loading";
    loadingIndicator.style.cssText = `
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: ${theme.colors.modalBackground}; z-index: 1;
    `;
    loadingIndicator.innerHTML = `
      <div style="
        width: 36px; height: 36px; border-radius: 50%;
        border: 3px solid ${theme.colors.brand100}; border-top-color: ${theme.colors.brand600};
        animation: fanfest-spin 0.8s linear infinite;
      "></div>
      <style>@keyframes fanfest-spin { to { transform: rotate(360deg); } }</style>
    `;

    const iframe = document.createElement("iframe");
    iframe.src = authModalUrl;
    iframe.style.cssText = "width: 100%; height: 100%; border: none;";
    iframe.title = "FanFest Authentication";

    iframe.onload = () => {
      if (loadingIndicator.parentElement) {
        loadingIndicator.parentElement.removeChild(loadingIndicator);
      }
    };

    iframe.onerror = () => {
      if (loadingIndicator.parentElement) {
        loadingIndicator.parentElement.removeChild(loadingIndicator);
      }
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText = `
        position: absolute; inset: 0;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        padding: 24px; text-align: center; font-family: sans-serif;
      `;
      errorDiv.innerHTML = `
        <p style="color: ${theme.colors.brandText}; margin-bottom: 12px;">Unable to load the authentication page.</p>
        <a href="${authModalUrl}" target="_blank" rel="noopener noreferrer"
          style="color: ${theme.colors.brand600}; text-decoration: underline;">
          Open in new tab
        </a>
      `;
      container.appendChild(errorDiv);
    };

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.setAttribute("aria-label", "Close authentication modal");
    closeButton.style.cssText = `
      position: absolute; top: 0.5rem; right: 0.5rem; z-index: 2;
      width: 24px; height: 24px;
      border: none; background: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.125rem; line-height: 1; color: ${theme.colors.brand700};
      padding: 8px; flex-shrink: 0;
    `;
    closeButton.textContent = "\u00D7";
    closeButton.addEventListener("mouseenter", () => {
      closeButton.style.color = theme.colors.brand800;
    });
    closeButton.addEventListener("mouseleave", () => {
      closeButton.style.color = theme.colors.brand700;
    });
    closeButton.addEventListener("click", () => this.close());

    this.#escapeHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") this.close();
    };
    document.addEventListener("keydown", this.#escapeHandler);

    container.appendChild(loadingIndicator);
    container.appendChild(iframe);
    container.appendChild(closeButton);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    this.#overlay = overlay;

    closeButton.focus();

    this.#logger.debug("Auth modal opened");
  }

  close() {
    if (!this.isOpen) {
      return;
    }

    this.#logger.debug("Closing auth modal");

    if (this.#escapeHandler) {
      document.removeEventListener("keydown", this.#escapeHandler);
      this.#escapeHandler = null;
    }

    if (this.#overlay?.parentElement) {
      this.#overlay.parentElement.removeChild(this.#overlay);
    }
    this.#overlay = null;

    document.body.style.overflow = this.#previousOverflow;

    this.#previouslyFocused?.focus();
    this.#previouslyFocused = null;

    this.#logger.debug("Auth modal closed");
  }
}
